/**
 * Turns MainSearchController's debug/test-mode JSON (activeApiCodesData/parsedData/
 * finalParsedData/requestStruct) into a structured, human-readable report — no LLM call, just
 * a deterministic reader of the fields the backend already computes (in particular each
 * pipeline entry's own `how` string, and each direct_airline entry's `type` — see
 * MainSearchHelpers.php's buildActiveApiEntry()/buildDirectAirlineEntry()).
 *
 * Returns { bucketReports: BucketReport[], raw } where each BucketReport is:
 *   {
 *     key,                 // e.g. "Request1_DOM-OW"
 *     kindLabel,            // plain-language bucket category (see describeBucketKind)
 *     kindDescription,
 *     countryCodes,         // string[]
 *     entries: [{ source, label, region, code, type, refIds, reasonText }],
 *     isEmpty,
 *     emptyReasonText,
 *   }
 */

function describeBucketKind(bucketKey) {
  if (/-CA(-NR)?$/.test(bucketKey) || /-CA_/.test(bucketKey)) {
    return {
      kindLabel: "Connecting Airport (CA)",
      kindDescription:
        "This searches via a connecting hub airport — the trip is split into two legs (Origin → Hub, Hub → Destination) and the results are combined. Only pipelines with 'Allow Connecting OW Hit' enabled, and airlines with a matching route through that hub, are considered.",
    };
  }
  if (/-VIA$/.test(bucketKey)) {
    return {
      kindLabel: "Via Airport",
      kindDescription:
        "The user (or route data) specified a transit/via airport. A direct-airline route only qualifies here if its own via-airport list fully covers the requested via airport(s).",
    };
  }
  if (/-OWOW/.test(bucketKey)) {
    return {
      kindLabel: "Combination (One-way + One-way)",
      kindDescription:
        "Two independent one-way fares (one per direction/leg) get combined into a single itinerary — the airline flying the outbound leg does NOT have to be the same as the one flying the return/next leg.",
    };
  }
  if (/-NR$/.test(bucketKey)) {
    return {
      kindLabel: "Nearest Airport",
      kindDescription:
        "One of the requested airports had no eligible hit, so nearby alternate airports (within the configured radius) are substituted and searched instead.",
    };
  }
  return {
    kindLabel: "Direct / Pure",
    kindDescription:
      "The exact route the user asked for, searched as-is — no connecting hub, no via-airport, no nearby-airport substitution.",
  };
}

/**
 * Detailed, specific "why" for an Admin user — names the actual pipeline/working-material/api
 * data row involved (not just a generic template repeated for every row), so two different
 * hits never read as the same boilerplate sentence. Still plain-language (no raw DB-dump), but
 * every noun in it is a real value pulled from this specific entry: pipeline data-row id,
 * working-material name, api name/code, which environments it's enabled for, and whether it
 * actually has a usable token — the backend's own `how` string (kept verbatim as reasonText/raw
 * for Developer mode) has the same facts but in developer shorthand
 * (pipeline_data_id=2, working_material.id=2, etc.), not sentence form.
 */
function adminReasonForPipelineEntry(entry) {
  const region = entry.pipeline_name || "your active region";
  const wm = entry.wm_name || "a working material";
  const api = entry.api_name || entry.code || "this API";
  const dataId = entry.pipeline_data_id ?? "?";
  const envs = entry.environments_supported;
  const tokenNote = entry.token_flight
    ? ""
    : " ⚠ No active flight token is set for it, so this hit may still fail at call time despite qualifying here.";

  let base;
  if (entry.type === "switched") {
    base =
      `Pipeline data row #${dataId} links your active region (${region}) directly to ${api} ` +
      `through working material "${wm}" — this is a same-region, non-shared match, and that ` +
      `pipeline data row's status is active.`;
  } else if (entry.type === "soto") {
    const regionLinkId = entry.pipeline_region_id ?? "?";
    base =
      `${api} isn't configured for ${region} directly. It qualified via SOTO ` +
      `(Sell-Other-Territory-Owner) sharing: pipeline_regions row #${regionLinkId} (the ` +
      `country-link row you'd find in Admin Panel → Air Api's Controller → linked-regions ` +
      `popup) is what let it in — pipeline data row #${dataId}, configured for a different ` +
      `region, has ${wm} explicitly marked to also sell into the country involved in this ` +
      `search.`;
  } else if (entry.type === "global-soto") {
    base =
      `${api} qualified as the GLOBAL DEFAULT (is_default=true) row for ${region} — pipeline ` +
      `data row #${dataId}, working material "${wm}". This means either you're browsing in ` +
      `"Global" mode, or your own region had no direct/SOTO row for this api, so the ` +
      `global-default row was used instead.`;
  } else {
    base = `Matched pipeline data row #${dataId} (${api} / "${wm}") with match type "${entry.type}".`;
  }

  return base + (envs ? ` Enabled for: ${envs}.` : "") + tokenNote;
}

function adminReasonForDirectAirlineEntry(entry) {
  const region = entry.direct_airline_region || "your active region";
  const name = entry.direct_airline_api_name || entry.api_name || "This airline";
  const dataId = entry.direct_airline_api_data_id ?? "?";
  const envs = entry.environments_supported;
  const tokenNote = entry.token_flight
    ? ""
    : " ⚠ No active flight token is set for it, so this hit may still fail at call time despite qualifying here.";

  let base;
  if (entry.type === "switched") {
    base =
      `${name}'s own direct-airline data row #${dataId} is configured for ${region} (your ` +
      `active region) — a direct, non-shared link between this airline and this exact route.`;
  } else if (entry.type === "soto") {
    base =
      `${name} isn't directly configured for ${region}. It qualified via SOTO sharing: direct` +
      `-airline data row #${dataId} — configured for a different region — is explicitly marked ` +
      `to also sell into the country involved in this search.`;
  } else if (entry.type === "global-soto") {
    base =
      `${name} qualified as the GLOBAL DEFAULT (is_default=true) direct-airline row #${dataId} ` +
      `for ${region} — used because either you're browsing in "Global" mode, or your own ` +
      `region had no direct/SOTO row for this airline.`;
  } else {
    base = `Matched direct-airline data row #${dataId} (${name}) with match type "${entry.type}".`;
  }

  return base + (envs ? ` Enabled for: ${envs}.` : "") + tokenNote;
}

/**
 * Normalizes one entry from the backend's rejectedPipelines[bucketKey] array (see
 * buildActiveApiCodesData()/collectPipelineApiEntries()/collectSotoApiEntriesForCountry()/
 * filterEntriesByEnabledModules() in MainSearchHelpers.php — each pushes {pipeline_id,
 * pipeline_name, type, reason}) into the same {label, region, type, reasonText, raw} shape the
 * "included" side uses, so both lists can share rendering code.
 */
function summarizeRejectedPipeline(entry) {
  return {
    label: entry.pipeline_name
      ? `${entry.pipeline_name} pipeline`
      : entry.pipeline_id
      ? `Pipeline #${entry.pipeline_id}`
      : "Unnamed pipeline candidate",
    region: entry.pipeline_name || "—",
    type: entry.type || "—",
    reasonText: entry.reason || "No reason recorded.",
    raw: entry,
  };
}

/**
 * Direct-airline counterpart of summarizeRejectedPipeline() — normalizes one entry from the
 * backend's rejectedDirectAirlines[bucketKey] array (see findEligibleDirectAirlinesForEntry()/
 * buildActiveDirectAirlineData() in MainSearchHelpers.php — each pushes
 * {direct_airline_api_id, airline_name, reason}). Only populated for the flat/generic bucket
 * builder (DOM-OW, DOM-OW-NR, INT-OW, INT-OW-NR, INT-OW-VIA, DOM-ML-NR-N) — CA/RD/ML's grouped
 * builders don't log rejections yet.
 */
function summarizeRejectedDirectAirline(entry) {
  return {
    label: entry.airline_name
      ? entry.airline_name
      : entry.direct_airline_api_id
      ? `Airline #${entry.direct_airline_api_id}`
      : "Route-level rejection",
    region: "—",
    type: "direct_airline",
    reasonText: entry.reason || "No reason recorded.",
    raw: entry,
  };
}

function reasonForDirectAirlineEntry(entry) {
  const name = entry.direct_airline_api_name || entry.api_name || "This airline";
  const region = entry.direct_airline_region || "an unspecified region";

  if (entry.type === "switched") {
    return `${name}'s own data row for your active region (${region}) matched directly — it's linked to the exact region you're searching from/as.`;
  }
  if (entry.type === "soto") {
    return `${name} was pulled in via SOTO (Sell-Other-Territory-Owner) sharing — its data row for ${region} is configured to also serve the country involved in this search, even though that's not your own region.`;
  }
  if (entry.type === "global-soto") {
    return `${name} was pulled in as the GLOBAL DEFAULT fallback (is_default=true row for ${region}) — used because either you're browsing in "Global" mode, or your own region had no direct/SOTO match for this airline.`;
  }
  return `${name} matched with type "${entry.type}".`;
}

/**
 * Turns one finalParsedData row's route fields into a structured route descriptor (not a plain
 * string) — kept structured so the UI can render the Origin/Destination airport codes as their
 * own hoverable elements (showing airport name/city/country on hover) instead of buried inside
 * a static sentence. finalParsedData rows are the raw parsedData candidate
 * (Origin/Destination/Date/...) spread together with the matched "Api" entry (see
 * buildFinalParsedDataForBucket()/...ForGroupedBucket() in MainSearchHelpers.php), so every
 * field this reads is already on the row as-is.
 */
function routeLabelForRow(row, legNote) {
  if (!row) return null;
  return {
    origin: row.Origin || "?",
    destination: row.Destination || "?",
    date: row.Date || null,
    isNearby: Boolean(row.nearestCode),
    nearestDistanceKm: row.Distance_inKM ?? null,
    legNote: legNote || null,
  };
}

/** Same identity a qualified hit (activeApiCodesData entry) and a finalParsedData row's "Api"
 * field share — used to match "which candidate routes did THIS specific hit end up paired
 * against". */
function apiIdentity(apiLike) {
  if (!apiLike) return null;
  const isPipeline = apiLike.source === "pipeline" || apiLike.pipeline_id != null;
  return isPipeline
    ? `pipeline:${apiLike.pipeline_data_id}`
    : `direct_airline:${apiLike.direct_airline_api_data_id}`;
}

/**
 * Finds every route this qualified hit will actually be queried against, by cross-referencing
 * the bucket's finalParsedData rows (each row = one candidate route + the api it matched)
 * against this entry's own identity. Handles both the flat-bucket shape (array of rows) and the
 * *-CA grouped shape (array of {position: [rows]} groups — see
 * buildFinalParsedDataForGroupedBucket()'s docblock).
 */
function collectMatchedRoutes(bareBucketKey, finalParsedForBucket, rawEntry) {
  const wantId = apiIdentity(rawEntry);
  if (!wantId || !Array.isArray(finalParsedForBucket)) return [];

  if (/-CA$/.test(bareBucketKey)) {
    const legNames = ["leg 1", "leg 2 (same day)", "leg 2 (next day)"];
    const routes = [];
    finalParsedForBucket.forEach((group) => {
      Object.entries(group || {}).forEach(([position, matches]) => {
        (Array.isArray(matches) ? matches : []).forEach((row) => {
          if (apiIdentity(row.Api) === wantId) {
            routes.push(routeLabelForRow(row, legNames[Number(position)] || `leg ${position}`));
          }
        });
      });
    });
    return routes;
  }

  // "Pure" multi-leg buckets pair every leg of one trip together, all sharing ONE Api at
  // row[0].Api (see MainSearchController.php's matching [0]['Api'] indexing) instead of each
  // leg carrying its own — round-trip's buildFinalParsedDataForRoundTripPureBucket() (INT-RD/
  // INT-RD-NR, always 2 legs) and multiway's N-leg generalization of it (INT-ML-N
  // "pure" main-arm buckets, 2-4 legs) both produce this shape. A flat row (independent-legs
  // buckets, buildFinalParsedDataForGroupedBucket()) never has it, so branch on Array.isArray.
  return finalParsedForBucket.flatMap((row) => {
    if (Array.isArray(row)) {
      if (apiIdentity(row[0]?.Api) !== wantId) return [];
      return row
        .map((leg, idx) =>
          routeLabelForRow(leg, row.length === 2 ? ["Going", "Returning"][idx] : `Leg ${idx + 1}`)
        )
        .filter(Boolean);
    }
    return apiIdentity(row.Api) === wantId ? [routeLabelForRow(row)] : [];
  });
}

function summarizeEntry(entry, bareBucketKey, finalParsedForBucket) {
  const isPipeline = entry.source === "pipeline" || entry.pipeline_id != null;
  const base = { source: isPipeline ? "pipeline" : "direct_airline" };

  const matchedRoutes = collectMatchedRoutes(bareBucketKey, finalParsedForBucket, entry);
  const withMatchedRoutes = (obj) => ({
    ...obj,
    matchedRoutes,
    matchedRoutesCount: matchedRoutes.length,
  });

  if (isPipeline) {
    return withMatchedRoutes({
      ...base,
      label: `${entry.api_name || entry.wm_name || "Unknown API"} (${entry.code || "?"})`,
      region: entry.pipeline_name || "—",
      code: entry.code,
      type: entry.type,
      refIds: entry.request_ref_id,
      dataId: entry.pipeline_data_id,
      regionLinkId: entry.pipeline_region_id,
      wmName: entry.wm_name,
      environmentsSupported: entry.environments_supported,
      tokenPresent: Boolean(entry.token_flight),
      reasonText: entry.how || "No explanation recorded for this pipeline hit.",
      adminReasonText: adminReasonForPipelineEntry(entry),
      raw: entry,
    });
  }

  return withMatchedRoutes({
    ...base,
    label: `${entry.direct_airline_api_name || "Unknown Airline"} (${entry.code || "?"})`,
    region: entry.direct_airline_region || "—",
    code: entry.code,
    type: entry.type,
    refIds: entry.request_ref_id,
    dataId: entry.direct_airline_api_data_id,
    wmName: entry.wm_name,
    environmentsSupported: entry.environments_supported,
    tokenPresent: Boolean(entry.token_flight),
    reasonText: reasonForDirectAirlineEntry(entry),
    adminReasonText: adminReasonForDirectAirlineEntry(entry),
    raw: entry,
  });
}

/**
 * Finds the actual debug payload (the object with 'activeApiCodesData' on it) no matter how
 * many layers of wrapping the response came back with. Backend responses have been observed
 * wrapped as Symfony Response's own public properties ({headers, original, exception, ...})
 * instead of the plain JSON body — apparently from a JsonResponse object being re-encoded
 * somewhere in the dispatch chain rather than unwrapped — and that wrapping isn't always
 * present or always one level deep, so this walks a few known/likely spots instead of
 * assuming a fixed shape.
 */
function locatePayload(rawResponse) {
  // Deep, depth-bounded search for the first object anywhere in the tree that actually has a
  // non-empty "activeApiCodesData" object on it — the fixed-depth candidate list this used to
  // check (rawResponse/.original/.data/...) wasn't deep enough for every wrapping shape we've
  // seen come back (Symfony JsonResponse re-serialization can nest .original more than once,
  // sometimes inside .data too), and picking the wrong shallower object silently produced an
  // empty report. This walks the whole tree (breadth-first, capped) instead of guessing depth.
  const seen = new Set();
  const queue = [rawResponse];
  let bestFallback = null;
  let bestFallbackKeyCount = -1;

  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object" || seen.has(node)) continue;
    seen.add(node);

    const keys = Object.keys(node);
    if (keys.length > bestFallbackKeyCount) {
      bestFallbackKeyCount = keys.length;
      bestFallback = node;
    }

    if (
      "activeApiCodesData" in node &&
      node.activeApiCodesData &&
      typeof node.activeApiCodesData === "object" &&
      Object.keys(node.activeApiCodesData).length > 0
    ) {
      return node;
    }

    for (const key of keys) {
      const value = node[key];
      if (value && typeof value === "object" && !seen.has(value)) {
        queue.push(value);
      }
    }
  }

  // Nothing had a non-empty activeApiCodesData anywhere — fall back to whichever object in the
  // tree looked most "unwrapped" (most top-level keys), so raw-JSON view still shows the real
  // payload instead of {}.
  return bestFallback || rawResponse;
}

/**
 * How many raw candidate "slots" a parsedData bucket represents, before any pipeline/
 * direct-airline filtering — used purely for the summary's candidateRequests count. Flat
 * buckets (DOM-OW, DOM-OW-NR, ...) are just their own length; the *-CA bucket's parsedData
 * entries are themselves arrays of 3 leg-groups (leg1 / leg2-same-day / leg2-next-day), so
 * each parsedData row is really 3 candidate legs.
 */
function countCandidateSlots(bucketKey, parsedEntries) {
  if (!Array.isArray(parsedEntries)) return 0;
  if (/-CA$/.test(bucketKey)) {
    return parsedEntries.reduce(
      (sum, group) => sum + (Array.isArray(group) ? group.length : 0),
      0
    );
  }
  return parsedEntries.length;
}

/**
 * activeApiCodesData/countryCodes are keyed "Request1_DOM-OW", "Request2_DOM-OW-NR", ... (see
 * MainSearchController's $directAirlineRequestsByKey), but parsedData and requestStruct's
 * `name` field both use the bare bucket name ("DOM-OW", "DOM-OW-NR", ...) with no "RequestN_"
 * prefix — without stripping it, lookups into parsedData/requestStruct by the activeApiCodesData
 * key always miss, which is why "candidate routes checked" and "outbound calls" were showing 0
 * even when hits had qualified.
 */
function stripBucketPrefix(bucketKey) {
  return bucketKey.replace(/^Request\d+_/, "");
}

function tally(map, key) {
  const k = key || "Unknown";
  map[k] = (map[k] || 0) + 1;
}

/**
 * requestStruct's `api_entry` is the exact matched pipeline/direct_airline entry (see
 * MainSearchController — "api_entry" => $requestParams['Api']), so it has the same shape as an
 * activeApiCodesData entry and can be summarized the same way, just for a single outbound call
 * instead of a single qualified hit.
 */
function summarizeCallApiEntry(apiEntry) {
  if (!apiEntry) {
    return { source: "unknown", code: "Unknown", region: "—" };
  }
  const isPipeline = apiEntry.source === "pipeline" || apiEntry.pipeline_id != null;
  return {
    source: isPipeline ? "pipeline" : "direct_airline",
    code: apiEntry.code || "Unknown",
    region: isPipeline
      ? apiEntry.pipeline_name || "—"
      : apiEntry.direct_airline_region || "—",
  };
}

/**
 * Builds the aggregate numbers the user actually asked for: total hits broken down by bucket
 * kind (direct / nearest / connecting / via / combination), by API, and by region, plus how
 * many real outbound API calls (requestStruct) that turned into. Everything here is derived
 * straight from what the backend already computed (activeApiCodesData/parsedData/
 * requestStruct) — no guessing, no LLM.
 */
function buildSummary(data, bucketReports) {
  const requestStruct = data?.requestStruct || [];
  const parsedData = data?.parsedData || {};

  const byKindLabel = {};
  const byApi = {};
  const byRegion = {};
  const bySource = { pipeline: 0, direct_airline: 0 };
  let totalHits = 0;

  // Separate tallies for the actual outbound API calls (requestStruct) — NOT the same thing as
  // "hits qualified". A single qualified hit (one pipeline/direct-airline entry) can turn into
  // MANY outbound calls, because it gets paired against every matching candidate route in that
  // bucket (e.g. DOM-OW-NR: 1 qualified TBO hit x 13 nearby-airport candidates = 13 calls). So
  // these totals sum to totalOutboundApiCalls (28), while byApi/byRegion/byBucketKind above sum
  // to totalQualifiedHits (5) — two different, both-correct counts of two different things.
  const callsByKindLabel = {};
  const callsByApi = {};
  const callsByRegion = {};
  const callsBySource = { pipeline: 0, direct_airline: 0, unknown: 0 };

  const perBucket = bucketReports.map((bucket) => {
    const bareName = stripBucketPrefix(bucket.key);
    const candidateRequests = countCandidateSlots(bareName, parsedData[bareName]);
    const callsForBucket = requestStruct.filter((r) => r.name === bareName);
    const outboundCallsForBucket = callsForBucket.length;

    const bucketByApi = {};
    const bucketByRegion = {};
    const bucketBySource = { pipeline: 0, direct_airline: 0 };
    const bucketCallsByApi = {};
    const bucketCallsByRegion = {};

    bucket.entries.forEach((entry) => {
      totalHits += 1;
      tally(byKindLabel, bucket.kindLabel);
      tally(byApi, entry.code);
      tally(byRegion, entry.region);
      bySource[entry.source] += 1;

      tally(bucketByApi, entry.code);
      tally(bucketByRegion, entry.region);
      bucketBySource[entry.source] += 1;
    });

    callsForBucket.forEach((call) => {
      const callInfo = summarizeCallApiEntry(call.api_entry);
      tally(callsByKindLabel, bucket.kindLabel);
      tally(callsByApi, callInfo.code);
      tally(callsByRegion, callInfo.region);
      callsBySource[callInfo.source] = (callsBySource[callInfo.source] || 0) + 1;

      tally(bucketCallsByApi, callInfo.code);
      tally(bucketCallsByRegion, callInfo.region);
    });

    return {
      key: bucket.key,
      kindLabel: bucket.kindLabel,
      candidateRequests,
      qualifiedHits: bucket.entries.length,
      rejectedNote:
        bucket.entries.length === 0 && candidateRequests > 0
          ? `${candidateRequests} candidate route(s) were checked, but 0 qualified — see this bucket's "why nothing matched" note below.`
          : null,
      outboundApiCalls: outboundCallsForBucket,
      byApi: bucketByApi,
      byRegion: bucketByRegion,
      bySource: bucketBySource,
      callsByApi: bucketCallsByApi,
      callsByRegion: bucketCallsByRegion,
    };
  });

  return {
    totalCandidateRequests: perBucket.reduce((s, b) => s + b.candidateRequests, 0),
    totalQualifiedHits: totalHits,
    totalOutboundApiCalls: requestStruct.length,
    byBucketKind: byKindLabel,
    byApi,
    byRegion,
    bySource,
    callsByBucketKind: callsByKindLabel,
    callsByApi,
    callsByRegion,
    callsBySource,
    perBucket,
  };
}

export function explainSearchDebugData(rawResponse) {
  const data = locatePayload(rawResponse);
  const activeApiCodesData = data?.activeApiCodesData || {};
  const countryCodes = data?.countryCodes || {};
  const requestStruct = data?.requestStruct || [];
  const userRegion = data?.user_region ?? "—";

  const finalParsedData = data?.finalParsedData || {};
  const rejectedPipelinesByBucket = data?.rejectedPipelines || {};
  const rejectedDirectAirlinesByBucket = data?.rejectedDirectAirlines || {};

  const bucketReports = Object.keys(activeApiCodesData).map((bucketKey) => {
    const { kindLabel, kindDescription } = describeBucketKind(bucketKey);
    const bareName = stripBucketPrefix(bucketKey);
    const entriesRaw = Array.isArray(activeApiCodesData[bucketKey])
      ? activeApiCodesData[bucketKey]
      : [];
    const entries = entriesRaw.map((entry) =>
      summarizeEntry(entry, bareName, finalParsedData[bareName])
    );
    const codes = countryCodes[bucketKey] || [];

    // "Why NOT included" — every pipeline/direct-airline candidate the backend actually
    // considered for this bucket but rejected, with a specific per-candidate reason (see
    // buildActiveApiCodesData()/findEligibleDirectAirlinesForEntry() and friends in
    // MainSearchHelpers.php). Direct-airline rejections are only logged for the flat/generic
    // bucket builder for now — CA/RD/ML's grouped builders don't populate this yet.
    const rejectedPipelines = Array.isArray(rejectedPipelinesByBucket[bucketKey])
      ? rejectedPipelinesByBucket[bucketKey]
      : [];
    const rejectedDirectAirlines = Array.isArray(rejectedDirectAirlinesByBucket[bucketKey])
      ? rejectedDirectAirlinesByBucket[bucketKey]
      : [];
    const totalRejected = rejectedPipelines.length + rejectedDirectAirlines.length;

    let emptyReasonText = null;
    if (entries.length === 0) {
      const countryList = codes.join(", ") || "the countries in this request";
      if (totalRejected > 0) {
        emptyReasonText =
          `No pipeline or direct-airline hit qualified for "${bucketKey}". ${totalRejected} ` +
          `candidate(s) were actually considered and rejected (${rejectedPipelines.length} pipeline, ` +
          `${rejectedDirectAirlines.length} direct-airline) — see the "Why NOT included" list below ` +
          `for the exact reason per candidate.`;
      } else {
        emptyReasonText =
          `No pipeline or direct-airline hit qualified for "${bucketKey}" ` +
          `(user region: ${data?.user_region ?? "—"}, countries involved: ${countryList}), and no ` +
          `candidate was even considered for this bucket for either side — meaning no pipeline is ` +
          `linked to ${countryList} at all via pipeline_regions, AND no direct-airline route exists ` +
          `for this exact origin↔destination pair at all.`;
      }
    }

    return {
      key: bucketKey,
      kindLabel,
      kindDescription,
      countryCodes: codes,
      entries,
      isEmpty: entries.length === 0,
      emptyReasonText,
      rejectedPipelines: rejectedPipelines.map(summarizeRejectedPipeline),
      rejectedDirectAirlines: rejectedDirectAirlines.map(summarizeRejectedDirectAirline),
    };
  });

  const summary = buildSummary(data, bucketReports);

  return {
    userRegion,
    requestStructCount: requestStruct.length,
    bucketReports,
    summary,
    raw: data,
  };
}
